/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useEffect, useState } from 'react';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import {
  useServicesContext,
  useSvd,
  FilterContextProvider,
  cleanupFilterURLHash,
  entryFilters,
  getRangeScope,
  RangeScope,
  FilterResponse,
  useDesktopOrMobileView,
  SeoFilterValue,
  OneCMSLayoutWrapper,
  useMandatoryAreaSearch,
  hasFilterHashMatch,
  useClientServerUtils,
} from '@oneaudi/vtp-shared';
import { useDispatch } from 'react-redux';
import { AudiStockcarsStoreServiceV1 } from '@oneaudi/stockcars-store-service';
import { ConfigurationServiceV1 } from '@oneaudi/vtp-configuration-service';
import { ContentService } from '@oneaudi/content-service';
import { QuickFilters } from './components/quickFilters/subcomponents/QuickFilters';
import Toolbar from './components/toolbar/Toolbar';
import styled from 'styled-components';
import TilesWrapper from './components/Tiles/TilesWrapper';
import useGoogleAutocomplete from './hooks/useGoogleAutocomplete';
import { FiltersAndTilesWrapper } from '../FeatureApp.styles';
import { ToolBarSkeleton } from './components/Tiles/Skeleton/ToolBarSkeleton';
import { OneCMSContent } from './types/oneCMS';

interface FeatureAppProps {
  trackingService?: TrackingServiceV2;
  seoFilters: SeoFilterValue[];
}

const FeatureApp: React.FC<FeatureAppProps> = ({
  trackingService,
  seoFilters,
}: FeatureAppProps) => {
  const configurationService = useServicesContext().getAdditionalService(
    'vtp-configuration-service'
  ) as ConfigurationServiceV1;
  const storeService = useServicesContext().getAdditionalService(
    'audi-stockcars-store-service'
  ) as AudiStockcarsStoreServiceV1;
  const contentService = useServicesContext().getAdditionalService(
    'audi-content-service'
  ) as ContentService;
  const { quickFilters, locationFilterConfig_mapsAuthQueryParams } = contentService.getContent()
    .fields as OneCMSContent;
  const { filterHandlerOnly, appContext } = contentService?.getContent()?.fields as OneCMSContent;

  const { filterHandlerOnly: filterHandlerOnlyConfig } = useServicesContext()
    .featureAppConfig as any;
  const dispatch = useDispatch();
  const { getConfiguration, getEnvironmentConfig } = useServicesContext();
  const { svd } = useSvd();
  const isDesktop: boolean = useDesktopOrMobileView('1024px') === 'desktop';

  const vtpConfiguration = getConfiguration();

  const envConfig = getEnvironmentConfig();

  const [filterData, setFilterData] = useState<FilterResponse | undefined>(undefined);
  const [rangeScope, setRangeScope] = useState<RangeScope[]>();
  const [libraryLoaded, setLibraryLoaded] = useState(false);

  const { isClient } = useClientServerUtils();

  const { appendMasPortal } = useMandatoryAreaSearch(libraryLoaded);

  useEffect(() => {
    if (trackingService) {
      // eslint-disable-next-line no-param-reassign
      trackingService.featureAppName = __FEATURE_APP_NAME__;

      const readyEvent = {
        event: {
          attributes: {
            componentName: __FEATURE_APP_NAME__,
            elementName: '',
            label: '',
            clickID: '',
            pos: '',
            targetURL: '',
            value: '',
          },
          eventInfo: {
            eventAction: 'feature_app_ready',
            eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
          },
        },
      };

      trackingService.track(readyEvent);
    }

    const quickFiltersArray: any = [];

    if (quickFilters?.fields) {
      Object.values(quickFilters.fields).forEach((filterNameType: any) => {
        if (filterNameType !== '') {
          const lastIndex = filterNameType.lastIndexOf('.');
          const filterName = filterNameType.substring(0, lastIndex);
          const filterType = filterNameType.substring(lastIndex + 1);
          const filterObject = { filter: filterName, filterType };
          quickFiltersArray.push(filterObject);
        }
      });
    }

    if (
      (quickFiltersArray.some((filter: any) => filter.filterType === 'location') ||
        configurationService?.getConfiguration()?.enableMandatoryAreaSearch) &&
      locationFilterConfig_mapsAuthQueryParams
    ) {
      useGoogleAutocomplete(locationFilterConfig_mapsAuthQueryParams).then(() => {
        if (!libraryLoaded) setLibraryLoaded(true);
      });
    }
  }, []);

  useEffect(() => {
    if (!svd || !vtpConfiguration || !envConfig) {
      return undefined;
    }
    const fetchFilters = async () => {
      const filterResponse = await entryFilters(
        storeService.store,
        configurationService,
        dispatch,
        vtpConfiguration,
        envConfig,
        svd,
        seoFilters
      );
      setFilterData(filterResponse!);
      cleanupFilterURLHash();
    };

    if (!filterData || hasFilterHashMatch()) {
      fetchFilters().then();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', fetchFilters);
      return () => window.removeEventListener('hashchange', fetchFilters);
    }
    return undefined;
  }, [svd, filterData]);

  useEffect(() => {
    const fetchRangeScope = async () => {
      const rangeScopeArray = await getRangeScope(
        envConfig!.scs.baseUrl,
        vtpConfiguration!.scs!.scsMarketPath!,
        envConfig!.scs.defaultApiVersion,
        svd
      );
      setRangeScope(rangeScopeArray);
    };
    if (svd) fetchRangeScope().then();
  }, [svd]);

  if (!filterData || filterHandlerOnly || filterHandlerOnlyConfig) {
    return null;
  }

  return (
    <OneCMSLayoutWrapper useUnifiedLibrary>
      <FilterContextProvider
        configurationService={configurationService}
        rangeScopes={rangeScope}
        initialFilterData={filterData}
        isPlp
      >
        {isClient ? <Toolbar /> : <ToolBarSkeleton />}
        <FiltersAndTilesWrapper>
          {appContext === 'results' && isDesktop && <QuickFilters />}
          <TilesWrapper />
        </FiltersAndTilesWrapper>
        {appendMasPortal()}
      </FilterContextProvider>
    </OneCMSLayoutWrapper>
  );
};

export default FeatureApp;

export const Layout = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
`;
export const QuickFilterContainer = styled.div`
  flex-basis: 25%;
  flex-shrink: 0;
  background-color: lightblue;
  display: none;
  @media screen and (min-width: 1024px) {
    display: flex;
  }
`;
